(() => {
class ATFPromoMotionToggle extends HTMLElement {
  connectToggle(selector, pausedAttribute) {
    this.toggle = this.querySelector(selector);
    if (!this.toggle) return false;

    this.pausedAttribute = pausedAttribute;
    this.userPaused = this.hasAttribute(pausedAttribute);
    this.toggle.setAttribute('aria-pressed', String(this.userPaused));
    this.toggle.setAttribute(
      'aria-label',
      this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel,
    );
    this.handleToggle = () => {
      this.userPaused = !this.userPaused;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.setAttribute(
        'aria-label',
        this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel,
      );
      this.toggleAttribute(this.pausedAttribute, this.userPaused);
      this.sync?.();
    };
    this.toggle.addEventListener('click', this.handleToggle);
    return true;
  }

  disconnectToggle() {
    this.toggle?.removeEventListener('click', this.handleToggle);
  }
}

if (!customElements.get('atf-promo-reassurance')) {
  customElements.define('atf-promo-reassurance', class extends ATFPromoMotionToggle {
    connectedCallback() {
      if (this.initialized || !this.connectToggle('[data-reassurance-toggle]', 'data-paused')) return;
      this.initialized = true;
    }

    disconnectedCallback() {
      this.disconnectToggle();
      this.initialized = false;
    }
  });
}

if (!customElements.get('atf-promo-carousel')) {
  customElements.define('atf-promo-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-carousel-track]');
      this.slides = [...this.querySelectorAll('.atf-promo__card')];
      this.progress = this.querySelector('[data-carousel-progress]');
      if (!this.track || this.slides.length < 2 || !this.progress) return;

      this.initialized = true;
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Math.min(80, Math.max(16, Number(this.dataset.speed) || 24));
      this.manualInteraction = false;
      this.inViewport = false;
      this.activated = false;
      this.primed = false;
      this.frameId = 0;
      this.lastFrame = 0;
      this.autoPosition = 0;
      this.slideExtent = 0;
      this.pointerStart = null;
      this.pointerPaused = false;
      this.recycling = false;
      this.settleTimer = 0;
      this.resumeTimer = 0;

      this.setManualInteraction = () => {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = 0;
        this.activated = true;
        this.manualInteraction = true;
        this.toggleAttribute('data-manual-carousel', true);
        this.stop();
        this.scheduleResume();
      };
      this.handlePointerDown = (event) => {
        if (!this.mobile.matches || this.reducedMotion.matches || event.isPrimary === false) return;
        this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.pointerPaused = true;
        this.stop();
      };
      this.handlePointerMove = (event) => {
        if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;
        if (Math.abs(deltaX) > Math.abs(deltaY)) this.setManualInteraction();
        else {
          this.pointerPaused = false;
          this.sync();
        }
        this.pointerStart = null;
      };
      this.handlePointerEnd = () => {
        this.pointerStart = null;
        if (!this.pointerPaused) return;
        this.pointerPaused = false;
        this.sync();
      };
      this.handleWheel = (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.setManualInteraction();
      };
      this.handleKeydown = (event) => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
          this.setManualInteraction();
        }
      };
      this.handleScroll = () => {
        this.updateProgress();
        if (this.manualInteraction && !this.recycling) this.scheduleSettle();
      };
      this.handleScrollEnd = () => this.settleManualPosition();
      this.handleChange = () => this.sync();
      this.handleVisibility = () => this.sync();
      this.handleResize = () => {
        this.measureTrack();
        if (this.primed && !this.recycling) {
          this.track.scrollLeft = this.slideExtent;
          this.autoPosition = this.track.scrollLeft;
        }
        this.updateProgress();
      };

      this.track.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      this.track.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.track.addEventListener('pointerup', this.handlePointerEnd, { passive: true });
      this.track.addEventListener('pointercancel', this.handlePointerEnd, { passive: true });
      this.track.addEventListener('wheel', this.handleWheel, { passive: true });
      this.track.addEventListener('keydown', this.handleKeydown);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('scrollend', this.handleScrollEnd, { passive: true });
      this.mobile.addEventListener('change', this.handleChange);
      this.reducedMotion.addEventListener('change', this.handleChange);
      document.addEventListener('visibilitychange', this.handleVisibility);

      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.activated) {
          this.activated = true;
          this.prepareLoop();
        }
        this.inViewport = this.activated && entry.isIntersecting;
        this.sync();
      }, { rootMargin: '0px', threshold: [0, .01] });
      this.observer.observe(this);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      }
      this.measureTrack();
      this.updateProgress();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.stop();
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('pointermove', this.handlePointerMove);
      this.track.removeEventListener('pointerup', this.handlePointerEnd);
      this.track.removeEventListener('pointercancel', this.handlePointerEnd);
      this.track.removeEventListener('wheel', this.handleWheel);
      this.track.removeEventListener('keydown', this.handleKeydown);
      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('scrollend', this.handleScrollEnd);
      this.mobile.removeEventListener('change', this.handleChange);
      this.reducedMotion.removeEventListener('change', this.handleChange);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.settleTimer);
      clearTimeout(this.resumeTimer);
      this.initialized = false;
    }

    canMove() {
      return this.mobile.matches
        && !this.reducedMotion.matches
        && !this.manualInteraction
        && !this.pointerPaused
        && this.inViewport
        && !document.hidden;
    }

    sync() {
      this.stop();
      if (!this.mobile.matches || this.reducedMotion.matches) {
        this.restoreOrder();
        this.updateProgress();
        return;
      }
      if (this.activated && !this.primed) this.prepareLoop();
      else this.measureTrack();
      if (this.canMove()) {
        this.autoPosition = this.track.scrollLeft;
        this.frameId = requestAnimationFrame((time) => this.advance(time));
      }
    }

    stop() {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.lastFrame = 0;
    }

    restoreOrder() {
      this.recycling = true;
      this.slides.forEach((slide) => this.track.append(slide));
      this.track.scrollLeft = 0;
      this.autoPosition = 0;
      this.primed = false;
      this.measureTrack();
      this.recycling = false;
    }

    prepareLoop() {
      if (!this.mobile.matches || this.reducedMotion.matches) return;
      this.recycling = true;
      this.slides.forEach((slide) => this.track.append(slide));
      this.measureTrack();
      this.track.prepend(this.track.lastElementChild);
      this.measureTrack();
      this.track.scrollLeft = this.slideExtent;
      this.autoPosition = this.track.scrollLeft;
      this.primed = true;
      this.recycling = false;
      this.updateProgress();
    }

    measureTrack() {
      const first = this.track.firstElementChild;
      const second = first?.nextElementSibling;
      if (!first) return;
      this.slideExtent = second
        ? Math.max(1, second.offsetLeft - first.offsetLeft)
        : Math.max(1, first.getBoundingClientRect().width);
    }

    rotateForward(position = this.track.scrollLeft) {
      if (!this.slideExtent) return;
      this.recycling = true;
      this.track.append(this.track.firstElementChild);
      this.track.scrollLeft = Math.max(0, position - this.slideExtent);
      this.autoPosition = this.track.scrollLeft;
      this.recycling = false;
    }

    rotateBackward(position = this.track.scrollLeft) {
      if (!this.slideExtent) return;
      this.recycling = true;
      this.track.prepend(this.track.lastElementChild);
      this.track.scrollLeft = position + this.slideExtent;
      this.autoPosition = this.track.scrollLeft;
      this.recycling = false;
    }

    scheduleSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settleManualPosition(), 160);
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.resumeTimer = 0;
        this.manualInteraction = false;
        this.toggleAttribute('data-manual-carousel', false);
        this.sync();
      }, 3000);
    }

    settleManualPosition() {
      clearTimeout(this.settleTimer);
      this.settleTimer = 0;
      if (!this.mobile.matches || !this.primed || this.recycling) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const tolerance = Math.max(2, this.slideExtent * .08);
      if (this.track.scrollLeft >= maximum - tolerance) this.rotateForward(this.track.scrollLeft);
      else if (this.track.scrollLeft <= tolerance) this.rotateBackward(this.track.scrollLeft);
      this.updateProgress();
      if (this.manualInteraction) this.scheduleResume();
    }

    updateProgress() {
      if (!this.progress || !this.slideExtent) return;
      const orderedSlides = [...this.track.children];
      const trackPosition = Math.max(0, Math.min(orderedSlides.length - 1, this.track.scrollLeft / this.slideExtent));
      const lowerSlot = Math.floor(trackPosition);
      const fraction = trackPosition - lowerSlot;
      const fromIndex = Math.max(0, this.slides.indexOf(orderedSlides[lowerSlot]));
      const toIndex = Math.max(0, this.slides.indexOf(orderedSlides[Math.min(lowerSlot + 1, orderedSlides.length - 1)]));
      let logicalPosition = fromIndex;
      if (toIndex === (fromIndex + 1) % this.slides.length) logicalPosition += fraction;
      else if (toIndex === (fromIndex - 1 + this.slides.length) % this.slides.length) logicalPosition -= fraction;
      logicalPosition = ((logicalPosition % this.slides.length) + this.slides.length) % this.slides.length;
      const available = Math.max(0, this.progress.parentElement.clientWidth - this.progress.clientWidth);
      this.progress.style.transform = `translate3d(${available * logicalPosition / this.slides.length}px, 0, 0)`;
    }

    advance(time) {
      if (!this.canMove()) return;
      if (!this.lastFrame) {
        this.lastFrame = time;
        this.autoPosition = this.track.scrollLeft;
      }
      const elapsed = Math.min(40, time - this.lastFrame);
      this.lastFrame = time;
      let nextPosition = this.autoPosition + this.speed * elapsed / 1000;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (nextPosition >= maximum && maximum > 0) {
        const overflow = nextPosition - maximum;
        this.track.scrollLeft = maximum;
        this.rotateForward(maximum);
        nextPosition = this.track.scrollLeft + overflow;
      }
      this.autoPosition = nextPosition;
      this.track.scrollLeft = nextPosition;
      this.updateProgress();
      this.frameId = requestAnimationFrame((nextTime) => this.advance(nextTime));
    }
  });
}

if (!customElements.get('atf-promo-tile-rotator')) {
  customElements.define('atf-promo-tile-rotator', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.variants = [...this.querySelectorAll('[data-promo-variant]')];
      if (this.variants.length < 2) return;

      this.initialized = true;
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.timer = 0;
      this.inViewport = false;
      this.handleChange = () => this.sync();
      this.handleVisibility = () => this.sync();
      this.mobile.addEventListener('change', this.handleChange);
      this.reducedMotion.addEventListener('change', this.handleChange);
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        this.sync();
      }, { rootMargin: '120px 0px', threshold: [0, .01] });
      this.observer.observe(this);
      this.show(0);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      clearTimeout(this.timer);
      this.mobile.removeEventListener('change', this.handleChange);
      this.reducedMotion.removeEventListener('change', this.handleChange);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.observer?.disconnect();
      this.initialized = false;
    }

    show(index) {
      this.activeIndex = index;
      this.variants.forEach((variant, variantIndex) => {
        const active = variantIndex === index;
        variant.classList.toggle('is-active', active);
        variant.setAttribute('aria-hidden', String(!active));
        if (variant.hasAttribute('href')) variant.setAttribute('tabindex', active ? '0' : '-1');
      });
    }

    sync() {
      clearTimeout(this.timer);
      this.timer = 0;
      if (this.reducedMotion.matches || document.hidden || !this.inViewport) return;
      const seconds = Number(this.mobile.matches ? this.dataset.mobileInterval : this.dataset.desktopInterval) || 5;
      this.timer = setTimeout(() => {
        this.show((this.activeIndex + 1) % this.variants.length);
        this.sync();
      }, Math.min(15, Math.max(3, seconds)) * 1000);
    }
  });
}

if (!customElements.get('atf-promo-search-feedback')) {
  customElements.define('atf-promo-search-feedback', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('.atf-promo__search-form');
      this.input = this.form?.querySelector('input[name="q"]');
      this.button = this.form?.querySelector('[data-search-submit]');
      if (!this.form || !this.input || !this.button) return;

      this.initialized = true;
      this.placeholderSuffix = this.querySelector('[data-search-placeholder-suffix]');
      this.placeholderOptions = [1, 2, 3, 4, 5, 6, 7, 8]
        .map((index) => this.getAttribute(`data-placeholder-${index}`)?.trim())
        .filter(Boolean);
      this.placeholderInterval = Math.min(8, Math.max(2, Number(this.dataset.placeholderInterval) || 3)) * 1000;
      this.placeholderIndex = 0;
      this.placeholderTimer = 0;
      this.placeholderFadeTimer = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.rotatePlaceholders = this.dataset.rotatePlaceholders === 'true'
        && this.placeholderSuffix
        && this.placeholderOptions.length > 0;
      this.handleSubmit = (event) => {
        if (event.defaultPrevented || !this.form.checkValidity() || !this.input.value.trim()) return;
        this.toggleAttribute('data-submitting', true);
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
      };
      this.syncPlaceholder = () => {
        this.toggleAttribute('data-search-has-value', Boolean(this.input.value));
        this.schedulePlaceholder();
      };
      this.handlePlaceholderVisibility = () => this.schedulePlaceholder();
      this.reset = () => {
        this.toggleAttribute('data-submitting', false);
        this.button.removeAttribute('aria-busy');
        this.button.disabled = false;
        this.syncPlaceholder();
      };
      this.form.addEventListener('submit', this.handleSubmit);
      window.addEventListener('pageshow', this.reset);
      if (this.rotatePlaceholders) {
        this.input.placeholder = '';
        this.placeholderSuffix.textContent = this.placeholderOptions[0];
        this.toggleAttribute('data-rotating-placeholder', true);
        this.input.addEventListener('input', this.syncPlaceholder);
        this.reducedMotion.addEventListener('change', this.handlePlaceholderVisibility);
        document.addEventListener('visibilitychange', this.handlePlaceholderVisibility);
        this.syncPlaceholder();
      }
    }

    schedulePlaceholder() {
      clearTimeout(this.placeholderTimer);
      clearTimeout(this.placeholderFadeTimer);
      this.placeholderTimer = 0;
      this.placeholderFadeTimer = 0;
      this.placeholderSuffix?.classList.remove('is-changing');
      if (!this.rotatePlaceholders
        || this.placeholderOptions.length < 2
        || this.reducedMotion.matches
        || document.hidden
        || this.input.value) return;
      this.placeholderTimer = setTimeout(() => this.changePlaceholder(), this.placeholderInterval);
    }

    changePlaceholder() {
      this.placeholderSuffix.classList.add('is-changing');
      this.placeholderFadeTimer = setTimeout(() => {
        this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderOptions.length;
        this.placeholderSuffix.textContent = this.placeholderOptions[this.placeholderIndex];
        this.placeholderSuffix.classList.remove('is-changing');
        this.placeholderFadeTimer = 0;
        this.schedulePlaceholder();
      }, 160);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      window.removeEventListener('pageshow', this.reset);
      this.input?.removeEventListener('input', this.syncPlaceholder);
      this.reducedMotion?.removeEventListener('change', this.handlePlaceholderVisibility);
      document.removeEventListener('visibilitychange', this.handlePlaceholderVisibility);
      clearTimeout(this.placeholderTimer);
      clearTimeout(this.placeholderFadeTimer);
      this.initialized = false;
    }
  });
}

if (!customElements.get('atf-promo-finder-drawer')) {
  customElements.define('atf-promo-finder-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.toggle = this.querySelector('[data-finder-toggle]');
      this.region = this.querySelector('[data-finder-region]');
      this.template = this.querySelector('[data-finder-template]');
      this.mount = this.querySelector('[data-finder-mount]');
      if (!this.toggle || !this.region || !this.template || !this.mount) return;

      this.initialized = true;
      this.expanded = false;
      this.handleToggle = () => this.setExpanded(!this.expanded);
      this.handleKeydown = (event) => {
        if (event.key !== 'Escape' || !this.expanded) return;
        this.setExpanded(false);
        this.toggle.focus();
      };
      this.toggle.addEventListener('click', this.handleToggle);
      this.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.handleToggle);
      this.removeEventListener('keydown', this.handleKeydown);
      this.initialized = false;
    }

    hydrate() {
      if (!this.template) return;
      this.mount.append(this.template.content.cloneNode(true));
      this.template.remove();
      this.template = null;
    }

    setExpanded(expanded) {
      this.expanded = expanded;
      if (expanded) this.hydrate();
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.region.setAttribute('aria-hidden', String(!expanded));
      this.region.toggleAttribute('inert', !expanded);
      this.toggleAttribute('data-finder-open', expanded);
      if (expanded) {
        requestAnimationFrame(() => requestAnimationFrame(() => this.scrollDrawerIntoView()));
      }
    }

    scrollDrawerIntoView() {
      const regionTop = this.region.getBoundingClientRect().top;
      const desiredTop = window.innerHeight * .56;
      const distance = Math.min(180, Math.max(0, regionTop - desiredTop));
      if (!distance) return;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({ top: distance, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  });
}
})();
